/**
 * Shared shapes for the supplier audit export. This file holds JSDoc typedefs
 * only, so every module in the feature (loader, shaping, row builders, UI)
 * agrees on one structure. See docs/supplier-audit-export.md for the mapping
 * from these fields back to Firestore.
 *
 * All dates are epoch milliseconds (or null) so the pure modules never depend
 * on Firestore Timestamp objects.
 */

/**
 * A hotel, course or transfer supplier as seen by the audit.
 *
 * @typedef {Object} AuditSupplier
 * @property {string} id            Firestore document id (the "GHD venue ID")
 * @property {"hotel"|"course"|"transfer"} kind
 * @property {string} name          "(Unknown / removed supplier)" when the doc is gone
 * @property {string} email
 * @property {string} currency      "GBP" | "EUR" | "USD" | ""
 * @property {string} region        From the linked CMS record's location, "" if unknown
 * @property {string} country       From the linked CMS record's location, "" if unknown
 * @property {boolean} missing      True when the supplier document no longer exists
 * @property {boolean} cmsLinked    True when a CMS holiday/course links to this supplier
 */

/**
 * The latest entry from an item's confirmations subcollection.
 *
 * @typedef {Object} AuditConfirmation
 * @property {"CONFIRMED"|"NOT_CONFIRMED"|""} status  "" when no entry exists
 * @property {string} reference       Supplier's booking reference
 * @property {string} contact         Supplier contact who confirmed (confirmed_by)
 * @property {number|null} receivedMillis
 * @property {string} notes
 */

/**
 * A supplier invoice linked to an item via itemIDs.
 *
 * @typedef {Object} AuditInvoice
 * @property {string} id
 * @property {number|null} amount
 * @property {string} currency
 * @property {string} status          PAID | UNPAID | PARTIAL | CREDIT | REQUESTED | ""
 * @property {number|null} paidMillis
 * @property {string} externalReference
 * @property {string} internalReference
 */

/**
 * One hotel stay on a booking (bookings/{id}/hotels/{hotelID}).
 *
 * @typedef {Object} AuditHotel
 * @property {string} id
 * @property {AuditSupplier} supplier
 * @property {number|null} checkInMillis
 * @property {number|null} checkOutMillis   checkIn + nights
 * @property {number} nights
 * @property {string} boardTypeKey          Raw board_type key
 * @property {string} boardType             Label from BOARD_TYPES, "" if unset
 * @property {string} rooms                 "2 x Twin Room, 1 x Single Room"
 * @property {number} roomCount
 * @property {string} mealTimes             Preferred dinner times (direct bookings), "" otherwise
 * @property {string} comments              Comments for the hotel
 * @property {string} reservationStatus     "" | "sent" | "approved" | "rejected"
 * @property {string} reservationComments
 * @property {AuditConfirmation} confirmation
 * @property {boolean} supplierConfirmed    confirmation.status === CONFIRMED || reservationStatus === approved
 * @property {number|null} cost             total_cost in the supplier's currency
 * @property {string} currency
 * @property {AuditInvoice[]} invoices
 */

/**
 * One round of golf on a booking (bookings/{id}/rounds/{roundID}).
 *
 * @typedef {Object} AuditRound
 * @property {string} id
 * @property {AuditSupplier} supplier
 * @property {number|null} teeTimeMillis    Date and time of play
 * @property {number} players               Booking golfers (not stored per round)
 * @property {string} buggiesKey            comp_buggies raw value
 * @property {string} buggies               Human label for comp_buggies
 * @property {boolean} buggiesBooked
 * @property {number|null} buggiesCost      Per golfer, prepaid only
 * @property {number|null} buggiesUnitCost  Raw buggies_cost per golfer, whatever the option (what the margins add up)
 * @property {string} comments
 * @property {string} reservationStatus     "" | "sent" | "approved" | "rejected"
 * @property {string} reservationComments
 * @property {AuditConfirmation} confirmation
 * @property {boolean} supplierConfirmed
 * @property {number|null} costPerGolfer
 * @property {number|null} cost             costPerGolfer x players
 * @property {string} currency
 * @property {AuditInvoice[]} invoices
 */

/**
 * Transfer arrangements on a booking (fields on the booking document).
 *
 * @typedef {Object} AuditTransport
 * @property {string} transferTypeKey       nothing | airport | golf | airport_and_golf | car_hire | euro_tunnel | ""
 * @property {string} transferType          Human label
 * @property {boolean} airportTransferRequired
 * @property {boolean} golfTransferRequired  transferType includes golf, or golf_shuttles
 * @property {AuditSupplier|null} supplier
 * @property {boolean} transfersBooked
 * @property {string} transfersReference
 * @property {string} reservationStatus     "" | "sent" | "approved" | "rejected"
 * @property {string} reservationComments
 * @property {AuditConfirmation} confirmation
 * @property {boolean} supplierConfirmed
 * @property {string} notes                 transfer_notes
 * @property {string} lookupProvider        transfers_lookup_selection.supplier_name
 * @property {string} lookupDescription     transfers_lookup_selection.short_description
 * @property {string} lookupPickupTime      transfers_lookup_selection.pickup_time
 * @property {number|null} cost             margins.transfers_cost, in the supplier currency (divide by the exchange rate for GBP)
 * @property {AuditInvoice[]} invoices
 * @property {boolean} eventTransferBooked
 * @property {string} eventTransferReference
 */

/**
 * Flight arrangements on a booking.
 *
 * @typedef {Object} AuditFlights
 * @property {boolean} required             margins.flights_cost > 0 or a flight object exists
 * @property {boolean} booked               flights_booked
 * @property {string} reference             flights_reference
 * @property {number|null} cost             margins.flights_cost (GBP)
 * @property {string} provider              Airline name from the PaxPort supplier code, "" otherwise
 * @property {string} outboundFlightNumber
 * @property {string} originAirport         IATA code
 * @property {string} arrivalAirport        IATA code
 * @property {number|null} departureMillis
 * @property {number|null} arrivalMillis
 * @property {string} returnFlightNumber
 * @property {number|null} returnDepartureMillis
 * @property {string} lookupArrivalTime     transfers_lookup_search.flight_ship_train_time
 * @property {string} lookupDestination     transfers_lookup_search.to_destination
 * @property {AuditConfirmation} confirmation
 */

/**
 * The booking's margins as stored. Hotel, round, buggy and transfer costs are
 * in the supplier currency; flights, miscellaneous and every derived figure
 * are GBP.
 *
 * @typedef {Object} AuditMargins
 * @property {number|null} customerPrice
 * @property {number} exchangeRate         Supplier currency per GBP, 1 when unset
 * @property {number|null} hotelsCost
 * @property {number|null} roundsCost
 * @property {number|null} buggiesCost
 * @property {number|null} transfersCost
 * @property {number|null} flightsCost
 * @property {number|null} miscellaneousCosts
 * @property {number|null} supplierInvoices  Total supplier cost in GBP
 * @property {number|null} profit
 * @property {number|null} agentsMargin      Agent commission
 */

/**
 * One booking with everything the audit needs.
 *
 * @typedef {Object} AuditBooking
 * @property {string} id
 * @property {string} reference             Booking reference as a string
 * @property {string} leadName
 * @property {string} leadEmail
 * @property {string} leadPhone
 * @property {string} agentName
 * @property {number|null} travelMillis     check_in
 * @property {number|null} bookedMillis
 * @property {boolean} confirmed            Booking-level confirmed flag
 * @property {number} golfers
 * @property {number} nonGolfers
 * @property {number} pax                   golfers + nonGolfers
 * @property {string} packageTitle          CMS holiday title for direct bookings, "" otherwise
 * @property {string} bookedLocation        booked_location
 * @property {string} enquiryLocation       enquiry.location
 * @property {string} packageSummary        "7 nights at X, Half Board, 3 rounds (A, B, C)"
 * @property {number|null} customerPrice    margins.customer_price
 * @property {AuditMargins} margins
 * @property {number|null} paidByClient
 * @property {number|null} balanceOutstanding
 * @property {boolean} resortsPaid
 * @property {string} eventTag
 * @property {AuditHotel[]} hotels          In hotels_order
 * @property {AuditRound[]} rounds          In rounds_order
 * @property {AuditTransport} transport
 * @property {AuditFlights} flights
 */

/**
 * A supplier row for the Suppliers tab and the picker.
 *
 * @typedef {AuditSupplier & { bookingCount: number, itemCount: number }} AuditSupplierSummary
 */

/**
 * The full result of one audit run.
 *
 * @typedef {Object} AuditResult
 * @property {AuditBooking[]} bookings
 * @property {AuditSupplierSummary[]} suppliers   Every supplier appearing in the window
 * @property {number} scanned                     Bookings read
 * @property {{ startMillis: number, endMillis: number|null }} window
 * @property {number} generatedMillis
 */

/**
 * Key used to identify a supplier across kinds (ids are unique per collection
 * but the picker mixes all three).
 *
 * @param {AuditSupplier} supplier
 * @returns {string} e.g. "hotel:abc123"
 */
export const supplierKey = (supplier) => `${supplier.kind}:${supplier.id}`

export const UNKNOWN_SUPPLIER_NAME = "(Unknown / removed supplier)"
export const UNNAMED_SUPPLIER_NAME = "(Unnamed supplier)"

export const TRANSFER_TYPES = {
	nothing: "No transfers",
	airport: "Airport",
	golf: "Golf",
	airport_and_golf: "Airport & Golf",
	car_hire: "Car hire included",
	euro_tunnel: "Euro tunnel",
}

export const BUGGY_OPTIONS = {
	no: "Not included",
	yes_reserved: "Reserved to pay locally",
	yes_greenfee: "Included in green fee",
	yes_prepaid: "Included and prepaid to GHD",
	true: "Yes",
	false: "No",
}
